import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, useMatch } from "react-router-dom";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  query,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
} from "firebase/firestore";
import { ref, getDownloadURL, uploadBytes } from "firebase/storage";
import { db, storage } from "../utils/firebase";
import { LOCALES, deloc, hasLocaleContent } from "../utils/locale";
import { extractTitle, extractThumbPath } from "../utils/contentHelpers";
import { useAuth } from "../../../context/AuthContext";
import { logActivity } from "../activityLog";
import { toast } from "react-toastify";
import { fnUrl } from "../../../utils/fnUrl";

const resolveCategoryThumbPath = (project: string, contentType: string, path: string) => {
  if (!path) return "";
  if (path.startsWith("content/")) return path;
  if (path.startsWith(`${contentType}/`)) return `content/${project}/${path}`;
  return `content/${project}/${contentType}/${path}`;
};

/** Thumbnail upload/preview for a category */
const CatThumb: React.FC<{ project: string; path: string; contentType: string; onChange: (path: string) => void }> = ({ project, path, contentType, onChange }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!path) { setPreviewUrl(null); return; }
    if (path.startsWith("http")) { setPreviewUrl(path); return; }
    let cancelled = false;
    const storagePath = resolveCategoryThumbPath(project, contentType, path);
    getDownloadURL(ref(storage, storagePath)).then((url) => { if (!cancelled) setPreviewUrl(url); }).catch(() => {});
    return () => { cancelled = true; };
  }, [project, path, contentType]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
      const base = file.name.slice(0, file.name.lastIndexOf(".")).toLowerCase().replace(/[^a-z0-9_-]/g, "_");
      const relativePath = `categories/${base}${ext}`;
      const storagePath = resolveCategoryThumbPath(project, contentType, relativePath);
      await uploadBytes(ref(storage, storagePath), file);
      const url = await getDownloadURL(ref(storage, storagePath));
      setPreviewUrl(url);
      onChange(relativePath);
    } catch (err) {
      console.error("[CatThumb] Upload error:", err);
    }
    setUploading(false);
  };

  return (
    <div className="cms__cat-thumb" onClick={() => fileInput.current?.click()} title="Upload category image">
      <input ref={fileInput} type="file" accept="image/*" style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ""; }} />
      {previewUrl
        ? <img src={previewUrl} alt="" className="cms__cat-thumb-img" />
        : <div className="cms__cat-thumb-empty">{uploading ? "…" : "+"}</div>}
    </div>
  );
};

interface ContentItem {
  id: string;
  title: string;
  category: string;
  categories: string[];
  mealRole: string;
  tags: string[];
  date: string;
  updatedAt: Date | null;
  updatedBy: string;
  locales: Record<string, "full" | "partial" | "empty">;
  thumbPath: string | null;
  thumbUrl: string | null;
  draft: boolean;
}

/** Pre-resolved thumbnail */
const ThumbImage: React.FC<{ url: string | null }> = ({ url }) => {
  if (!url) {
    return <div className="cms__item-thumb cms__item-thumb--empty" />;
  }
  return <img src={url} alt="" className="cms__item-thumb" />;
};

const PAGE_SIZE = 150;

const slugify = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

const setLocalizedTitleValue = (current: any, value: string) => {
  if (current && typeof current === "object") return { ...current, en: value };
  return value;
};

const getLocalizedValue = (value: any, locale: string) => {
  if (value && typeof value === "object") return value[locale] || "";
  return locale === "en" && typeof value === "string" ? value : "";
};

const ContentList = () => {
  const { project, contentType } = useParams<{ project: string; contentType: string }>();
  const navigate = useNavigate();
  const categoryRouteMatch = useMatch("/cms/:project/:contentType/categories");
  const { user, firebaseUser } = useAuth();
  const [items, setItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [cursors, setCursors] = useState<QueryDocumentSnapshot<DocumentData>[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [managedCategories, setManagedCategories] = useState<any[] | null>(null);
  const [newCatInput, setNewCatInput] = useState("");
  const [newSubcatInputs, setNewSubcatInputs] = useState<Record<string, string>>({});
  const [translatingCats, setTranslatingCats] = useState(false);
  const [catTranslateProgress, setCatTranslateProgress] = useState(0);
  const [catTranslateTotal, setCatTranslateTotal] = useState(0);
  const isCategoryEditMode = !!categoryRouteMatch;
  const switchCategoryFilter = (categoryId: string | null) => {
    const nextCategory = selectedCategory === categoryId ? null : categoryId;
    setSelectedCategory(nextCategory);
    if (page !== 0) {
      setCursors([]);
      setHasMore(false);
      loadPage(0);
    }
  };
  const renderLocaleBadges = (value: any) => (
    <div className="cms__cat-manager-locales">
      {LOCALES.map((loc) => {
        const filled = !!getLocalizedValue(value, loc)?.trim();
        return (
          <span
            key={loc}
            className={`cms__locale ${filled ? "cms__locale--filled" : "cms__locale--partial"}`}
            title={`${loc.toUpperCase()}: ${filled ? "translated" : "missing"}`}
          >
            {loc.toUpperCase()}
          </span>
        );
      })}
    </div>
  );

  const createNew = async () => {
    if (!project || !contentType) return;

    // Auto-generate unique ID
    const prefix = contentType.replace(/s$/, ""); // insights → insight
    const uid = crypto.randomUUID().slice(0, 8);
    const docId = `${prefix}_${uid}`;

    setCreating(true);
    try {
      const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
      const el = { en: "", es: "", de: "", fr: "" };
      const template = [
        {
          type: "group",
          background_color: null,
          content: [
            { type: "title", text: { ...el } },
            { type: "image", imageRef: "" },
            { type: "text", text: { ...el } },
          ],
        },
      ];
      await setDoc(doc(db, "content", project, contentType, docId), {
        content: template,
        meta: { category: "", tags: [], date: today, ...(contentType === "insights" ? { isNew: true } : {}) },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: user?.email || "cms",
      });
      logActivity(project, {
        action: "create",
        contentType,
        docId,
        user: user?.email || firebaseUser?.email || "unknown",
        photoURL: user?.photoURL || firebaseUser?.photoURL || undefined,
      });
      navigate(`/cms/${project}/${contentType}/${docId}`);
    } catch (err) {
      console.error("[ContentList] Create error:", err);
      alert("Failed to create document");
    }
    setCreating(false);
  };

  const loadPage = async (pageNum: number) => {
    if (!project || !contentType) return;
    setLoading(true);

    try {
      const colRef = collection(db, "content", project, contentType);
      const cursor = pageNum > 0 ? cursors[pageNum - 1] : undefined;
      const sortField = contentType === "tips" ? "order" : "createdAt";
      const sortDir = contentType === "tips" ? "asc" : "desc";
      const q = cursor
        ? query(colRef, orderBy(sortField, sortDir), startAfter(cursor), limit(PAGE_SIZE))
        : query(colRef, orderBy(sortField, sortDir), limit(PAGE_SIZE));
      const snapshot = await getDocs(q);

      const docs: ContentItem[] = [];
      snapshot.forEach((d) => {
        const data = d.data();
        const title = extractTitle(data, contentType!);
        const categoryIds = Array.isArray(data.meta?.category)
          ? data.meta.category.filter(Boolean)
          : (data.meta?.category ? [data.meta.category] : []);
        const tags = Array.isArray(data.meta?.tags)
          ? data.meta.tags.filter(Boolean)
          : (data.meta?.tags ? [data.meta.tags] : []);

        const localeStatus: Record<string, "full" | "partial" | "empty"> = {};
        for (const loc of LOCALES) {
          localeStatus[loc] = hasLocaleContent(data.content || [], loc) ? "full" : "empty";
        }

        const thumbPath = contentType === "mealplans" ? null : (data.meta?.thumb || null);

        docs.push({
          id: d.id,
          title,
          category: categoryIds[0] || "",
          categories: categoryIds,
          mealRole: data.meta?.meal_role || "",
          tags: tags,
          date: data.meta?.date || "",
          updatedAt: data.createdAt?.toDate?.() || data.updatedAt?.toDate?.() || null,
          updatedBy: data.updatedBy || "",
          locales: localeStatus,
          thumbPath,
          thumbUrl: null,
          draft: !!data.meta?.draft,
        });
      });

      // Resolve thumb URLs for this page
      await Promise.all(
        docs.map(async (item) => {
          if (!item.thumbPath) return;
          try {
            item.thumbUrl = await getDownloadURL(ref(storage, item.thumbPath));
          } catch {}
        })
      );

      // Save cursor for next page
      const lastVisible = snapshot.docs[snapshot.docs.length - 1];
      if (lastVisible) {
        setCursors((prev) => {
          const next = [...prev];
          next[pageNum] = lastVisible;
          return next;
        });
      }
      // Secondary sort: within same createdAt, sort by numeric ID desc
      docs.sort((a, b) => {
        const da = a.updatedAt?.getTime() || 0;
        const db = b.updatedAt?.getTime() || 0;
        if (da !== db) return db - da;
        const na = parseInt(a.id.replace(/\D/g, "")) || 0;
        const nb = parseInt(b.id.replace(/\D/g, "")) || 0;
        if (na && nb) return nb - na;
        return b.id.localeCompare(a.id);
      });

      setHasMore(snapshot.docs.length === PAGE_SIZE);
      setPage(pageNum);
      setItems(docs);
    } catch (err) {
      console.error("[ContentList] Error loading:", err);
    }

    setLoading(false);
  };

  const loadCategories = async () => {
    if (!project || !contentType) return;
    try {
      const snap = await getDoc(doc(db, "content", project, "_config", contentType));
      setManagedCategories(snap.exists() ? snap.data().categories || [] : []);
    } catch {
      setManagedCategories([]);
    }
  };

  const saveCategories = async (next: any[]) => {
    if (!project || !contentType) return;
    setManagedCategories(next);
    try {
      await setDoc(doc(db, "content", project, "_config", contentType), { categories: next }, { merge: true });
    } catch (err) {
      console.error("[ContentList] Save categories error:", err);
    }
  };

  const addCategory = () => {
    const name = newCatInput.trim();
    if (!name) return;
    const id = slugify(name);
    if ((managedCategories || []).some((c: any) => c.id === id)) return;
    saveCategories([...(managedCategories || []), { id, title: name, color: "#6366f1", emoji: "📌", subcategories: [] }]);
    setNewCatInput("");
  };

  const updateCategoryField = (catIndex: number, field: string, value: string) => {
    const next = [...(managedCategories || [])];
    const current = next[catIndex];
    if (!current) return;
    next[catIndex] = {
      ...current,
      [field]: field === "title" ? setLocalizedTitleValue(current.title, value) : value,
    };
    saveCategories(next);
  };

  const addSubcategory = (catIndex: number) => {
    const category = (managedCategories || [])[catIndex];
    if (!category) return;
    const name = (newSubcatInputs[category.id] || "").trim();
    if (!name) return;
    const id = slugify(name);
    const existing = Array.isArray(category.subcategories) ? category.subcategories : [];
    if (existing.some((sub: any) => sub.id === id)) return;
    const next = [...(managedCategories || [])];
    next[catIndex] = {
      ...category,
      subcategories: [...existing, { id, title: name }],
    };
    saveCategories(next);
    setNewSubcatInputs((prev) => ({ ...prev, [category.id]: "" }));
  };

  const updateSubcategory = (catIndex: number, subIndex: number, value: string) => {
    const category = (managedCategories || [])[catIndex];
    if (!category) return;
    const next = [...(managedCategories || [])];
    const subcategories = [...(Array.isArray(category.subcategories) ? category.subcategories : [])];
    const current = subcategories[subIndex];
    if (!current) return;
    subcategories[subIndex] = {
      ...current,
      title: setLocalizedTitleValue(current.title, value),
    };
    next[catIndex] = { ...category, subcategories };
    saveCategories(next);
  };

  const removeSubcategory = (catIndex: number, subIndex: number) => {
    const category = (managedCategories || [])[catIndex];
    if (!category) return;
    const subcategories = [...(Array.isArray(category.subcategories) ? category.subcategories : [])];
    subcategories.splice(subIndex, 1);
    const next = [...(managedCategories || [])];
    next[catIndex] = { ...category, subcategories };
    saveCategories(next);
  };

  const removeCategory = (catId: string) => {
    if (usedCategoryIds.has(catId)) return;
    saveCategories((managedCategories || []).filter((c: any) => c.id !== catId));
  };

  const translateCategories = async () => {
    if (!project || !contentType || !firebaseUser || !managedCategories?.length) return;
    const texts = managedCategories.flatMap((cat: any, i: number) => {
      const entries: Array<{ kind: "category" | "subcategory"; i: number; subIndex?: number; en: string }> = [];
      const en = typeof cat.title === "string" ? cat.title : cat.title?.en || "";
      if (en.trim()) entries.push({ kind: "category", i, en });
      const subs = Array.isArray(cat.subcategories) ? cat.subcategories : [];
      subs.forEach((sub: any, subIndex: number) => {
        const subEn = typeof sub.title === "string" ? sub.title : sub.title?.en || "";
        if (subEn.trim()) entries.push({ kind: "subcategory", i, subIndex, en: subEn });
      });
      return entries;
    });
    if (texts.length === 0) { toast.info("Nothing to translate"); return; }

    setTranslatingCats(true);
    setCatTranslateProgress(0);
    setCatTranslateTotal(texts.length);
    try {
      const idToken = await firebaseUser.getIdToken();
      const url = fnUrl("translateContent");
      const results: Array<{ kind: "category" | "subcategory"; i: number; subIndex?: number; locales: Record<string, string> }> = [];
      let completed = 0;
      const MAX_CONCURRENT = 5;
      for (let c = 0; c < texts.length; c += MAX_CONCURRENT) {
        const chunk = texts.slice(c, c + MAX_CONCURRENT);
        await Promise.all(
          chunk.map(async ({ kind, i, subIndex, en }) => {
            const resp = await fetch(url, {
              method: "POST",
              headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
              body: JSON.stringify({ key: kind === "category" ? `cat.${i}.title` : `cat.${i}.sub.${subIndex}.title`, text: en }),
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json();
            results.push({ kind, i, subIndex, locales: data.locales });
            completed++;
            setCatTranslateProgress(completed);
          })
        );
      }
      const next = managedCategories.map((cat: any, idx: number) => {
        const en = typeof cat.title === "string" ? cat.title : cat.title?.en || "";
        const categoryTranslation = results.find((r) => r.kind === "category" && r.i === idx);
        const subcategories = (Array.isArray(cat.subcategories) ? cat.subcategories : []).map((sub: any, subIndex: number) => {
          const subEn = typeof sub.title === "string" ? sub.title : sub.title?.en || "";
          const subTranslation = results.find((r) => r.kind === "subcategory" && r.i === idx && r.subIndex === subIndex);
          if (!subTranslation) return sub;
          return { ...sub, title: { en: subEn, ...subTranslation.locales } };
        });
        return {
          ...cat,
          title: categoryTranslation ? { en, ...categoryTranslation.locales } : cat.title,
          subcategories,
        };
      });
      await saveCategories(next);
      toast.success("Categories translated & saved");
    } catch (err: any) {
      console.error("[ContentList] Translate categories error:", err);
      toast.error(`Translation failed: ${err.message}`);
    }
    setTranslatingCats(false);
  };

  const usedCategoryIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of items) {
      if (item.categories?.length) {
        for (const catId of item.categories) ids.add(catId);
      } else if (item.category) {
        ids.add(item.category);
      }
    }
    return ids;
  }, [items]);

  useEffect(() => {
    setItems([]);
    setSearchQuery("");
    setSelectedCategory(null);
    setPage(0);
    setCursors([]);
    setHasMore(false);
    loadPage(0);
    loadCategories();
  }, [project, contentType]);

  // Build category list with counts from loaded items, enriched with managed category data
  const categories = useMemo(() => {
    const countMap = new Map<string, number>();
    for (const item of items) {
      const catIds = item.categories?.length ? item.categories : (item.category ? [item.category] : []);
      for (const catId of catIds) {
        countMap.set(catId, (countMap.get(catId) || 0) + 1);
      }
    }
    return (managedCategories || []).map((cat: any) => ({
      ...cat,
      count: countMap.get(cat.id) || 0,
    }));
  }, [items, managedCategories]);

  // Filter items by search + category
  const filteredItems = useMemo(() => {
    let result = items;

    if (selectedCategory) {
      result = result.filter((i) => {
        const catIds = i.categories?.length ? i.categories : (i.category ? [i.category] : []);
        return catIds.includes(selectedCategory);
      });
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (i) =>
          i.title.toLowerCase().includes(q) ||
          i.id.toLowerCase().includes(q) ||
          (i.categories?.length ? i.categories : (i.category ? [i.category] : [])).some((cat) => cat.toLowerCase().includes(q))
      );
    }

    return result;
  }, [items, selectedCategory, searchQuery]);

  const deleteDraft = async (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    if (!project || !contentType) return;
    if (!confirm(`Delete draft "${itemId}"?`)) return;
    try {
      const deleted = items.find((i) => i.id === itemId);
      await deleteDoc(doc(db, "content", project, contentType, itemId));
      logActivity(project, {
        action: "delete",
        contentType,
        docId: itemId,
        docTitle: deleted?.title || itemId,
        user: user?.email || firebaseUser?.email || "unknown",
        photoURL: user?.photoURL || firebaseUser?.photoURL || undefined,
      });
      setItems((prev) => prev.filter((i) => i.id !== itemId));
    } catch (err) {
      console.error("[ContentList] Delete error:", err);
      alert("Failed to delete");
    }
  };

  const formatDate = (d: Date | null) => {
    if (!d) return "—";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const categoryMap = useMemo(() => new Map((managedCategories || []).map((cat: any) => [cat.id, cat])), [managedCategories]);

  const renderItemCard = (item: ContentItem) => (
    <div key={item.id} className={`cms__item${item.draft ? " cms__item--draft" : ""}`} onClick={() => navigate(`/cms/${project}/${contentType}/${item.id}`)}>
      {contentType !== "mealplans" && <ThumbImage url={item.thumbUrl} />}
      <div className="cms__item-main">
        <div className="cms__item-title-row">
          <span className="cms__item-title">{item.title}</span>
        </div>
        <div className="cms__item-info">
          {contentType === "recipes" ? (() => {
            const allCats = Array.from(new Set(
              (item.categories?.length ? item.categories : [item.category, ...item.tags]).filter(Boolean)
            ));
            const orderMap = new Map((managedCategories || []).map((c: any, idx: number) => [c.id, idx]));
            const displayCats = allCats
              .filter((id) => orderMap.size === 0 || orderMap.has(id))
              .sort((a, b) => {
                const ai = orderMap.has(a) ? (orderMap.get(a) as number) : Number.MAX_SAFE_INTEGER;
                const bi = orderMap.has(b) ? (orderMap.get(b) as number) : Number.MAX_SAFE_INTEGER;
                if (ai !== bi) return ai - bi;
                return a.localeCompare(b);
              });
            if (!displayCats.length) return null;
            return (
              <div className="cms__item-tags">
                {displayCats.map((tag) => {
                  const catObj = (managedCategories || []).find((c: any) => c.id === tag);
                  return (
                    <span key={tag} className="cms__item-tag" style={catObj?.color ? { borderColor: catObj.color, color: catObj.color } : undefined}>
                      {catObj ? `${catObj.emoji} ${deloc(catObj.title)}` : tag}
                    </span>
                  );
                })}
                {item.mealRole && (
                  <span className="cms__item-tag">
                    {`Role: ${item.mealRole.charAt(0).toUpperCase()}${item.mealRole.slice(1)}`}
                  </span>
                )}
              </div>
            );
          })() : item.category && (() => {
            const catObj = (managedCategories || []).find((c: any) => c.id === item.category);
            return (
              <span className="cms__item-category" style={catObj?.color ? { color: catObj.color } : undefined}>
                {catObj ? `${catObj.emoji} ${deloc(catObj.title)}` : item.category}
              </span>
            );
          })()}
          {contentType !== "recipes" && item.tags.filter((tag) => tag !== item.category).length > 0 && (
            <div className="cms__item-tags">
              {item.tags.filter((tag) => tag !== item.category).map((tag) => {
                const catObj = (managedCategories || []).find((c: any) => c.id === tag);
                return (
                  <span key={tag} className="cms__item-tag" style={catObj?.color ? { borderColor: catObj.color, color: catObj.color } : undefined}>
                    {catObj ? `${catObj.emoji} ${deloc(catObj.title)}` : tag}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="cms__item-meta">
        <div className="cms__item-locales">
          {LOCALES.map((loc) => (
            <span
              key={loc}
              className={`cms__locale ${item.locales[loc] === "full" ? "cms__locale--filled" : "cms__locale--partial"}`}
              title={`${loc.toUpperCase()}: ${item.locales[loc]}`}
            >
              {loc.toUpperCase()}
            </span>
          ))}
        </div>
        <span className="cms__item-date">{formatDate(item.updatedAt)}</span>
        {item.draft && (
          <button className="cms__item-delete" onClick={(e) => deleteDraft(e, item.id)} title="Delete draft">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );

  const groupedListSections = useMemo(() => {
    if (contentType !== "recipes" && contentType !== "insights") return [];

    const getParentCategories = (item: ContentItem) => {
      const raw = item.categories?.length ? item.categories : (item.category ? [item.category] : []);
      return raw.filter((id) => categoryMap.has(id));
    };

    const sourceCategories = selectedCategory
      ? (managedCategories || []).filter((cat: any) => cat.id === selectedCategory)
      : (managedCategories || []);

    const sections = sourceCategories.map((cat: any) => {
      const catItems = filteredItems.filter((item) => getParentCategories(item).includes(cat.id));
      if (!catItems.length) return null;

      const subcategories = Array.isArray(cat.subcategories) ? cat.subcategories : [];
      const subSections = subcategories
        .map((sub: any) => ({
          ...sub,
          items: catItems.filter((item) => item.tags.includes(sub.id)),
        }))
        .filter((sub: any) => sub.items.length > 0);

      const usedSubIds = new Set(subSections.map((sub: any) => sub.id));
      const directItems = catItems.filter((item) => !item.tags.some((tag) => usedSubIds.has(tag)));

      return {
        ...cat,
        directItems,
        subSections,
      };
    }).filter(Boolean) as any[];

    const categorizedIds = new Set(sections.flatMap((section) => [...section.directItems, ...section.subSections.flatMap((sub: any) => sub.items)].map((item: ContentItem) => item.id)));
    const uncategorizedItems = filteredItems.filter((item) => !categorizedIds.has(item.id));
    if (selectedCategory === null && uncategorizedItems.length) {
      sections.push({
        id: "uncategorized",
        title: "Uncategorized",
        emoji: "",
        color: "#94a3b8",
        directItems: uncategorizedItems,
        subSections: [],
      });
    }

    return sections;
  }, [selectedCategory, contentType, managedCategories, filteredItems, categoryMap]);

  return (
    <div className="cms__content">

      {/* Category filter */}
      {(categories.length > 0 || (managedCategories && managedCategories.length > 0)) && (
        <div className="cms__categories">
          <button
            className={`cms__category-pill ${selectedCategory === null ? "cms__category-pill--active" : ""}`}
            onClick={() => switchCategoryFilter(null)}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              className={`cms__category-pill ${selectedCategory === cat.id ? "cms__category-pill--active" : ""}`}
              style={selectedCategory === cat.id ? { background: cat.color, borderColor: cat.color } : undefined}
              onClick={() => switchCategoryFilter(cat.id)}
            >
              {cat.emoji} {deloc(cat.title)}
            </button>
          ))}
          <button
            className={`cms__category-pill cms__category-pill--manage${isCategoryEditMode ? " cms__category-pill--active" : ""}`}
            onClick={() => navigate(isCategoryEditMode ? `/cms/${project}/${contentType}` : `/cms/${project}/${contentType}/categories`)}
            title="Manage categories"
            aria-label="Manage categories"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
        </div>
      )}

      {/* Category manager */}
      {isCategoryEditMode && (
        <div className="cms__cat-manager">
          <div className="cms__cat-manager-header">
            <div>
              <div className="cms__cat-manager-kicker">Categories</div>
              <div className="cms__cat-manager-heading">Shape the taxonomy for {contentType}</div>
              <div className="cms__cat-manager-subtitle">Organize parent categories and nested subcategories without the content list in view.</div>
            </div>
            <div className="cms__cat-manager-actions">
              <button
                className="cms__cat-manager-translate"
                onClick={translateCategories}
                disabled={translatingCats || !managedCategories?.length}
              >
                {translatingCats ? "Translating..." : "Translate all"}
              </button>
              <button className="cms__cat-manager-close" onClick={() => navigate(`/cms/${project}/${contentType}`)}>
                Done
              </button>
            </div>
          </div>
          <div className="cms__cat-manager-list">
            {(managedCategories || []).map((cat: any, idx: number) => {
              const inUse = usedCategoryIds.has(cat.id);
              const subcategories = Array.isArray(cat.subcategories) ? cat.subcategories : [];
              return (
                <div key={cat.id} className="cms__cat-manager-item" style={{ borderColor: cat.color }}>
                  <div className="cms__cat-manager-topline">
                    <CatThumb project={project!} path={cat.thumb_url || ""} contentType={contentType!} onChange={(path) => updateCategoryField(idx, "thumb_url", path)} />
                    <div className="cms__cat-manager-mainfields">
                      <div className="cms__cat-manager-row">
                        <input
                          className="cms__cat-manager-emoji"
                          value={cat.emoji || ""}
                          onChange={(e) => updateCategoryField(idx, "emoji", e.target.value)}
                          title="Emoji"
                        />
                        <label className="cms__cat-manager-color-btn" title="Category color" style={{ ["--cat-color" as any]: cat.color || "#6366f1" }}>
                          <input
                            type="color"
                            className="cms__cat-manager-color"
                            value={cat.color || "#6366f1"}
                            onChange={(e) => updateCategoryField(idx, "color", e.target.value)}
                            aria-label="Category color"
                          />
                        </label>
                        <input
                          className="cms__cat-manager-title"
                          value={deloc(cat.title)}
                          onChange={(e) => updateCategoryField(idx, "title", e.target.value)}
                          title="Title"
                        />
                        {renderLocaleBadges(cat.title)}
                      </div>
                    </div>
                    <button
                      className="cms__cat-manager-remove"
                      onClick={() => removeCategory(cat.id)}
                      disabled={inUse}
                      title={inUse ? "In use - remove content first" : "Remove"}
                      aria-label={`Remove ${deloc(cat.title) || cat.id}`}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>

                  <div className="cms__subcat-manager">
                    <div className="cms__subcat-manager-header">
                      <span className="cms__subcat-manager-title">Subcategories</span>
                      <span className="cms__subcat-manager-count">{subcategories.length}</span>
                    </div>
                    <div className="cms__subcat-manager-list">
                      {subcategories.map((sub: any, subIndex: number) => {
                        const subTitle = deloc(sub.title);
                        return (
                          <div key={sub.id} className="cms__subcat-row">
                            <div className="cms__subcat-row-main">
                              <div className="cms__subcat-row-line">
                                <input
                                  className="cms__subcat-row-input"
                                  value={subTitle}
                                  onChange={(e) => updateSubcategory(idx, subIndex, e.target.value)}
                                  title="Subcategory title"
                                />
                                {renderLocaleBadges(sub.title)}
                              </div>
                            </div>
                            <button
                              className="cms__subcat-row-remove"
                              onClick={() => removeSubcategory(idx, subIndex)}
                              aria-label={`Remove ${subTitle || sub.id}`}
                              title="Remove subcategory"
                            >
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                      {subcategories.length === 0 && <span className="cms__subcat-empty">No subcategories yet</span>}
                    </div>
                    <div className="cms__subcat-add">
                      <input
                        className="cms__subcat-add-input"
                        value={newSubcatInputs[cat.id] || ""}
                        onChange={(e) => setNewSubcatInputs((prev) => ({ ...prev, [cat.id]: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && addSubcategory(idx)}
                        placeholder="Add subcategory..."
                      />
                      <button className="cms__subcat-add-btn" onClick={() => addSubcategory(idx)} disabled={!(newSubcatInputs[cat.id] || "").trim()}>
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            {(!managedCategories || managedCategories.length === 0) && (
              <span className="cms__cat-manager-hint">No categories yet</span>
            )}
          </div>
          <div className="cms__cat-manager-add">
            <input
              className="cms__cat-manager-input"
              value={newCatInput}
              onChange={(e) => setNewCatInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCategory()}
              placeholder="New category..."
            />
            <button className="cms__cat-manager-btn" onClick={addCategory} disabled={!newCatInput.trim()}>Add</button>
          </div>
        </div>
      )}

      {!isCategoryEditMode && (
        <>
          <div className="cms__search">
            <input
              className="cms__search-input"
              type="text"
              placeholder="Search by title, ID or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {contentType !== "recipes" && contentType !== "mealplans" && (
              <button className="cms__new-btn" onClick={createNew} disabled={creating}>
                {creating ? "Creating..." : "+ New"}
              </button>
            )}
          </div>

          {loading || managedCategories === null ? (
            <div className="cms__loading">Loading...</div>
          ) : filteredItems.length === 0 ? (
            <div className="cms__empty">
              {items.length === 0 ? "No content found." : "No matches."}
            </div>
          ) : groupedListSections.length > 0 ? (
            <div className="cms__list cms__list--grouped">
              {groupedListSections.map((section) => (
                <section key={section.id} className="cms__group-section">
                  <div className="cms__group-header" style={section.color ? { borderColor: section.color } : undefined}>
                    <h3 className="cms__group-title" style={section.color ? { color: section.color } : undefined}>
                      {section.emoji ? `${section.emoji} ` : ""}{typeof section.title === "string" ? section.title : deloc(section.title)}
                    </h3>
                  </div>
                  {section.directItems.length > 0 && (
                    <div className="cms__group-items">
                      {section.directItems.map((item: ContentItem) => renderItemCard(item))}
                    </div>
                  )}
                  {section.subSections.map((sub: any) => (
                    <div key={sub.id} className="cms__subgroup-section">
                      <div className="cms__subgroup-header" style={section.color ? { borderColor: section.color } : undefined}>
                        <span className="cms__subgroup-title">{`${typeof section.title === "string" ? section.title : deloc(section.title)}: ${deloc(sub.title) || sub.id}`}</span>
                      </div>
                      <div className="cms__group-items">
                        {sub.items.map((item: ContentItem) => renderItemCard(item))}
                      </div>
                    </div>
                  ))}
                </section>
              ))}
              {(page > 0 || hasMore) && (
                <div className="cms__pagination">
                  <button
                    className="cms__pagination-btn"
                    onClick={() => loadPage(page - 1)}
                    disabled={page === 0 || loading}
                  >
                    ← Prev
                  </button>
                  <span className="cms__pagination-info">Page {page + 1}</span>
                  <button
                    className="cms__pagination-btn"
                    onClick={() => loadPage(page + 1)}
                    disabled={!hasMore || loading}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="cms__list">
              {filteredItems.map((item) => renderItemCard(item))}
              {(page > 0 || hasMore) && (
                <div className="cms__pagination">
                  <button
                    className="cms__pagination-btn"
                    onClick={() => loadPage(page - 1)}
                    disabled={page === 0 || loading}
                  >
                    ← Prev
                  </button>
                  <span className="cms__pagination-info">Page {page + 1}</span>
                  <button
                    className="cms__pagination-btn"
                    onClick={() => loadPage(page + 1)}
                    disabled={!hasMore || loading}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
      {translatingCats && (
        <div className="translate-overlay">
          <div className="translate-overlay__card">
            <div className="translate-overlay__icon">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2">
                <path d="m5 8 6 6"/><path d="m4 14 6-6 2-3"/><path d="M2 5h12"/>
                <path d="M7 2h1"/><path d="m22 22-5-10-5 10"/><path d="M14 18h6"/>
              </svg>
            </div>
            <p className="translate-overlay__counter">{catTranslateProgress} / {catTranslateTotal}</p>
            <div className="translate-overlay__track">
              <div
                className="translate-overlay__fill"
                style={{ width: `${catTranslateTotal ? (catTranslateProgress / catTranslateTotal) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentList;
